import type { ReactNode } from "react";
import { useFormatRelativeTime } from "../../hooks/useFormatRelativeTime";
import type { DisplayableSpace } from "../../hooks/useSpaceDisplayName";
import { useMakeUrl, routes } from "../../lib/urls";
import { Button } from "../Button";
import type { IconName } from "../Icon";
import { ListTable } from "../ListTable";
import { Modal } from "../Modal";
import { SearchField } from "../Settings/shared";
import { Text } from "../Text";
import { WorkflowBadge } from "../WorkflowBadge";
import { useMonitoringInteraction } from "./analytics";
import {
  filterWorkflowRowsByName,
  formatExecutionCount,
} from "./executionCounts";
import {
  DETAIL_MODAL_STYLE,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalHeaderRight,
  ModalTitle,
} from "./MonitoringDetailCard";
import { ListSection, NameBody, NameCell } from "./monitoringList";

type WorkflowListRow = {
  id: string;
  name: string;
  spaceId: string;
  badgeSeed: string | null;
  pausedAt: number | null;
  executionCount: number;
  lastRunAt: number | null;
};

type Props = {
  open: boolean;
  onClose: () => void;
  icon: IconName;
  title: string;
  space?: DisplayableSpace;
  sectionTitle: string;
  description: string;
  rows: readonly WorkflowListRow[];
  search: string;
  onSearchChange: (value: string) => void;
  emptyMessage: string;
  renderFooter: (visibleCount: number) => ReactNode;
  headerAction?: ReactNode;
};

export function WorkflowListModal({
  open,
  onClose,
  icon,
  title,
  space,
  sectionTitle,
  description,
  rows,
  search,
  onSearchChange,
  emptyMessage,
  renderFooter,
  headerAction,
}: Props) {
  const makeUrl = useMakeUrl();
  const formatRelativeTime = useFormatRelativeTime();
  const recordInteraction = useMonitoringInteraction();
  const visibleRows = filterWorkflowRowsByName(rows, search);

  return (
    <Modal open={open} onClose={onClose} style={DETAIL_MODAL_STYLE}>
      <ModalHeader>
        <ModalTitle icon={icon} title={title} space={space} />
        <ModalHeaderRight>
          {headerAction}
          <Button size="l" variant="secondary" icon="x" onClick={onClose} />
        </ModalHeaderRight>
      </ModalHeader>

      <ModalBody style={{ gap: 0 }}>
        <ListSection
          title={sectionTitle}
          description={description}
          action={
            <SearchField
              placeholder="Search workflows…"
              value={search}
              onChange={onSearchChange}
              width={280}
              autoFocus
            />
          }
        >
          <ListTable
            headers={{
              name: "Workflow",
              executions: "Executions",
              lastRun: "Last run",
            }}
            columns={{ name: "flex", executions: "md", lastRun: "md" }}
            emptyState={
              <Text variant="text-regular" color="var(--text-secondary)">
                {search ? "No workflows match your search." : emptyMessage}
              </Text>
            }
            items={visibleRows.map((row) => ({
              key: row.id,
              link: makeUrl(routes.space.workflow, {
                spaceId: row.spaceId,
                workflowId: row.id,
              }),
              onNavigate: () =>
                recordInteraction("workflows.workflow_link_click", {
                  targetType: "workflow",
                  targetId: row.id,
                }),
              name: (hover: boolean) => (
                <NameCell>
                  <WorkflowBadge
                    workflow={{
                      id: row.id,
                      badgeSeed: row.badgeSeed,
                      pausedAt: row.pausedAt,
                      branches: [{ name: "main" }],
                    }}
                    hover={hover}
                  />
                  <NameBody>
                    <Text variant="text-regular-md" ellipsis>
                      {row.name}
                    </Text>
                  </NameBody>
                </NameCell>
              ),
              executions: (
                <Text variant="text-small">
                  {formatExecutionCount(row.executionCount)}
                </Text>
              ),
              lastRun: (
                <Text variant="text-small">
                  {row.lastRunAt != null
                    ? formatRelativeTime(row.lastRunAt)
                    : "—"}
                </Text>
              ),
            }))}
          />
        </ListSection>
      </ModalBody>

      <ModalFooter>
        <Text variant="text-small" color="var(--text-secondary)">
          {renderFooter(visibleRows.length)}
        </Text>
        <Button size="l" variant="primary" onClick={onClose}>
          Done
        </Button>
      </ModalFooter>
    </Modal>
  );
}
